import { z } from "zod";

import { MetaDataSchema } from "@/schemas/meta-data";

import { YearlyOrMonthly } from "@/enum/company.enum";

import { SubscriptionSchema } from "../../Subscription/schema/subscription";
import { UserSchema } from "../../Users/schema/invite-user";

export const SeatKindSummarySchema = z.object({
	used: z.number(),
	included: z.number(),
	paid: z.number(),
	bonus: z.number(),
	total: z.number(),
});

/** LinkedIn / email seat capacity of a company, as the admin list row carries it (PREC-4063). */
export const CompanySeatSummarySchema = z.object({
	linkedin: SeatKindSummarySchema,
	email: SeatKindSummarySchema,
});

export type SeatKindSummary = z.infer<typeof SeatKindSummarySchema>;
export type CompanySeatSummary = z.infer<typeof CompanySeatSummarySchema>;

/**
 * The post-cancellation lifecycle state, resolved by the API with the same function that gates
 * access (PREC-4093). Never re-derived here from the raw dates: this lifecycle already carried
 * three disagreeing implementations of "may this company use the product" (PREC-4065).
 */
export const CompanyLifecycleStateSchema = z.enum([
	"ACTIVE",
	"GRACE",
	"ARCHIVED",
	"DUE_FOR_DELETION",
]);

export type CompanyLifecycleState = z.infer<typeof CompanyLifecycleStateSchema>;

/**
 * A user the list search matched, as the admin row shows them (PREC-4096).
 *
 * Deliberately not `UserSchema`: the API selects only the columns the row renders, so the full
 * user shape (status, 2FA, blocked) is absent and validating against it would reject the response.
 */
export const MatchedUserSchema = z.object({
	id: z.number(),
	firstName: z.string().optional(),
	lastName: z.string().optional(),
	email: z.string().optional(),
});

export type MatchedUser = z.infer<typeof MatchedUserSchema>;

export const CompaniesSchema = z.object({
	id: z.number(),
	name: z.string(),
	linkedinUrl: z.string().optional(),
	hasApolloApiKey: z.boolean(),
	hasLemlistApiKey: z.boolean(),
	hasHubspotApiKey: z.boolean(),
	admins: z.array(UserSchema),
	users: z.array(UserSchema),
	/**
	 * Every non-deleted user of the company, admins included. Server-counted: `admins` and
	 * `users` are role-filtered slices, so their lengths answer neither on their own.
	 */
	userCount: z.number().optional(),
	/**
	 * The users of this company that matched the search term, so a row can say *why* it matched.
	 * Empty when the term matched only the company name, and absent on the single-company GET.
	 */
	matchedUsers: z.array(MatchedUserSchema).optional(),
	/** Only on the list endpoint; the single-company GET does not carry it. */
	seats: CompanySeatSummarySchema.optional(),
	filtered: z.number(),
	yearlyOrMonthly: z.nativeEnum(YearlyOrMonthly),
	subscriptions: z.array(SubscriptionSchema),
	/** Lifecycle columns; only the admin list endpoint carries them (PREC-4093). */
	lifecycleState: CompanyLifecycleStateSchema.optional(),
	/** End of the read-only month — the advertised day-30 date, not the guard's fallback. */
	readOnlyUntil: z.string().nullable().optional(),
	archivedAt: z.string().nullable().optional(),
	deletionAt: z.string().nullable().optional(),
	demoEndsAt: z.string().optional(),
	demoEndsInDays: z.number().optional(),
	isDemo: z.boolean(),
	website: z.string().optional(),
	websiteScrapeCredits: z.number().optional(),
	credits: z.number().optional(),
	createdAt: z.string().optional(),
	createdBy: UserSchema.optional(),
	isOnboarded: z.boolean(),
	allowInstallments: z.boolean().optional(),
	allowBankTransfer: z.boolean().optional(),
});

export const CreateCompaniesSchema = z.object({
	name: z.string(),
	linkedinUrl: z.string().optional(),
	filtered: z.number().optional(),
	yearlyOrMonthly: z.nativeEnum(YearlyOrMonthly).optional(),
	subscription: SubscriptionSchema.optional(),
	subscriptionId: z.number().optional(),
	demoCredits: z.number().gte(0),
	demoEndsInDays: z.number().gte(0),
	website: z.string().optional(),
});

export const UpdateCompaniesSchema = z.object({
	id: z.number().optional(),
	name: z.string(),
	linkedinUrl: z.string().optional(),
	filtered: z.number().optional(),
	yearlyOrMonthly: z.nativeEnum(YearlyOrMonthly),
	subscriptionId: z.number().optional(),
	demoEndsInDays: z.number().gte(0).optional(),
	website: z.string().optional(),
	allowInstallments: z.boolean().optional(),
	allowBankTransfer: z.boolean().optional(),
});

export const CompaniesResponseSchema = z.object({
	data: z.array(CompaniesSchema),
	meta: MetaDataSchema,
});

export const TopupCompanySchema = z.object({
	id: z.number(),
	credits: z.number(),
	websiteScrapeCredits: z.number(),
});

export type Companies = z.infer<typeof CompaniesSchema>;

export type CreateCompanies = z.infer<typeof CreateCompaniesSchema>;

export type UpdateCompanies = z.infer<typeof UpdateCompaniesSchema>;

export type TopupCompany = z.infer<typeof TopupCompanySchema>;

export type CompaniesResponse = z.infer<typeof CompaniesResponseSchema>;
